/* eslint-disable @typescript-eslint/naming-convention */
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, forkJoin, Observable, of, skip, Subscription, take } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import { DiaryAnswersheetsDetailComponent } from '../diary-answersheets-detail/diary-answersheets-detail.component';
import { AnswersheetInterface } from '../../../models/interface/answersheet.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { DiaryInterface, VisualizedDataTypes } from '../../../models/interface/diary.interface';
import { DiaryStore } from '../../../store/diary/component-store/diary.store';
import { ActivityInterface } from 'src/app/models/interface/activity.interface';
import { HelperActivityService } from 'src/app/services/helper/helper-activity/helper-activity.service';
import { PageEvent } from '@angular/material/paginator';
import { QuestionnaireStore } from 'src/app/store/lesson-questionnaire/component-store/lesson-questionnaire.store';
import { ElementInterface } from 'src/app/models/interface/elements/element.interface';

Chart.register(zoomPlugin);

type BasicQuestionType = Exclude<VisualizedDataTypes, 'SensorData_steps'| 'TextDate_date' | 'TextDate_time' | 'TextDate_datetime'>;

type QuestionType = 'Date' | 'Time' | 'DateTime' | 'Slider' | 'QuestionTable';

type DataType = QuestionType | 'Pedometer';

type AnswerData = { value: any, type: DataType; };

type ChartDataPoint = { collectedAt: number; answers: Map<number, AnswerData> };

type VisualizableQuestion = { position: number; label: string };

type VisualizedData = { position: number; type: DataType; label: string };

/**
 * Component:
 * Displays a diary and its answer sheets;
 */
@Component({
  selector: 'app-diary-answersheets',
  templateUrl: './diary-answersheets.component.html',
  styleUrls: ['./diary-answersheets.component.scss'],
  providers: [QuestionnaireStore, DiaryStore],
})
export class DiaryAnswersheetsComponent implements OnInit, OnDestroy {
  @ViewChild('answersheetsDetailsPreviewContainer', { read: ViewContainerRef })
  answersheetsDetailsPreviewContainer: ViewContainerRef;
  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;

  private chart!: Chart<any>;

  public isLoading$: Observable<boolean>;
  public hasDiaryEntries$: Observable<boolean>;
  public hasDiaryActivities$: Observable<boolean>;

  public diaryAnswersheets: AnswersheetInterface[] = [];
  public answersheetsOfDiary$: Observable<AnswersheetInterface[]>;
  public diaryActivities$: Observable<ActivityInterface[]>;
  public selected = new UntypedFormControl(0);

  public activitiesSubject = new BehaviorSubject<ActivityInterface[]>([]);
  public pagedActivitySubject = new BehaviorSubject<ActivityInterface[]>([]);
  public pagedActivity$ = this.pagedActivitySubject.asObservable();

  private diaryInstanceId: number;
  private diary: DiaryInterface;
  private isManager: boolean;

  private elementsOfQuestionnaire$: Observable<ElementInterface[] | null>;
  private elements: ElementInterface[] = [];

  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private hasDiaryEntriesSubject = new BehaviorSubject<boolean>(false);
  private hasDiaryActivitiesSubject = new BehaviorSubject<boolean>(false);

  private subscriptions: Subscription[] = [];

  loadingChartData = true;

  // Visualized questions are sorted by position of their element, which is bigger than 0.
  // Since pedometer data is not linked to an element, but the answersheet, a pre-defined position is used.
  private readonly pedometerDataPosition = -1;

  allowsVisualization = false;
  hasChartData = false;
  private answerMap: ChartDataPoint[];
  private visualizedData: VisualizedData[];

  filter = {
    filter1: '',
    filter2: '',
  };

  visualizableQuestions: VisualizableQuestion[];

  visualizableQuestions1Subject = new BehaviorSubject<VisualizableQuestion[]>([]);
  visualizableQuestions1$ = this.visualizableQuestions1Subject.asObservable();
  visualizableQuestions2Subject = new BehaviorSubject<VisualizableQuestion[]>([]);
  visualizableQuestions2$ = this.visualizableQuestions2Subject.asObservable();

  visualizabledQuestion1: VisualizableQuestion;
  visualizabledQuestion2: VisualizableQuestion;

  constructor(
    private diaryStore: DiaryStore,
    private translateService: TranslateService,
    private helperService: HelperService,
    private questionnaireStore: QuestionnaireStore,
    private helperActivityService: HelperActivityService,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.hasDiaryEntries$ = this.hasDiaryEntriesSubject.asObservable();
    this.hasDiaryActivities$ = this.hasDiaryActivitiesSubject.asObservable();
    this.elementsOfQuestionnaire$ = this.questionnaireStore.elementsOfQuestionnaire$;
    this.answersheetsOfDiary$ = this.diaryStore.answersheetsOfDiary$;
    this.diaryActivities$ = this.diaryStore.diaryActivities$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperActivity() {
    return this.helperActivityService;
  }

  @Input()
  set _diary_instance_id(_diaryInstanceId: number) {
    this.diaryInstanceId = _diaryInstanceId;
  }

  @Input()
  set _diary(_diary: DiaryInterface) {
    this.diary = _diary;
  }

  @Input()
  set _is_manager(_is_manager: boolean) {
    this.isManager = _is_manager;
  }

  ngOnInit(): void {
    const visualizedDataTypes = this.diary.attributes.visualized_data_types;
    this.allowsVisualization = Array.isArray(visualizedDataTypes) && visualizedDataTypes.length > 0;

    if (this.isManager) {
      this.diaryStore.getActivitiesOfDiaryInstanceEM({
        diaryInstanceId: this.diaryInstanceId,
      });
    } else {
      this.diaryStore.getActivitiesOfDiaryInstance({
        diaryInstanceId: this.diaryInstanceId,
      });
    }
    this.subscriptions.push(
      this.diaryActivities$.pipe(skip(1), take(1)).subscribe((res) => {
        this.activitiesSubject.next(this.helper.getActivityByNewest(res));
        this.pagedActivitySubject.next(this.activitiesSubject.value.slice(0, 20));
      }),
    );

    this.questionnaireStore.getElementsOfQuestionnaire({
      questionnaireId: this.diary.attributes.questionnaire_id,
    });
    const diaryElements: Observable<ElementInterface[] | null> = this.elementsOfQuestionnaire$.pipe(
      skip(1),
      take(1),
      catchError(() => of(null)),
    );
    this.requestDiaryAnswersheets();
    const diaryAnswersheets = this.answersheetsOfDiary$.pipe(skip(1), take(1));
    this.subscriptions.push(
      forkJoin({ diaryElements, diaryAnswersheets }).subscribe((results) => {
        this.elements = results.diaryElements;
        if (this.allowsVisualization && results.diaryAnswersheets.length > 1) {
          this.setupChartData(this.elements, results.diaryAnswersheets);
          this.createChart(
            this.answerMap,
            this.visualizedData,
            this.visualizabledQuestion1,
            this.visualizabledQuestion2,
          );
        }
        this.loadingChartData = false;
        this.loadAnswersheetPreview(results.diaryAnswersheets);
      }),
    );
  }

  private setupChartData(elements: ElementInterface[] | null, diaryAnswersheets: AnswersheetInterface[]) {
    const visualizedData = this.getVisualizedData(elements);
    this.visualizableQuestions = this.getVisualizableData(visualizedData);
    this.visualizableQuestions1Subject.next(this.visualizableQuestions);
    this.visualizableQuestions2Subject.next(this.visualizableQuestions);
    this.visualizabledQuestion1 = this.visualizableQuestions[0];
    this.visualizabledQuestion2 = this.visualizableQuestions[1];
    this.answerMap = this.getAnswerMap(diaryAnswersheets, visualizedData);
    this.visualizedData = visualizedData;
  }

  private getVisualizedData(elements: ElementInterface[] | null): VisualizedData[] {
    const visualizableDataTypes = this.diary.attributes.visualized_data_types!;
    const visualizeDateQuestions = visualizableDataTypes.includes('TextDate_date');
    const visualizeTimeQuestions = visualizableDataTypes.includes('TextDate_time');
    const visualizeDateTimeQuestions = visualizableDataTypes.includes('TextDate_datetime');
    const basicQuestionTypes = this.filterBasicQuestionTypes(visualizableDataTypes);
    const visualizedData: VisualizedData[] = [];
    const visualizeSteps = visualizableDataTypes.includes('SensorData_steps');
    if (visualizeSteps) {
      visualizedData.push({
        position: this.pedometerDataPosition,
        type: 'Pedometer',
        label: this.translateService.instant('diary-answersheets.pedometer'),
      });
    }
    for (const e in elements) {
      const element = elements[e];
      if (!element.questiontype) {
        continue;
      }
      if (element.questiontype === 'TextDate') {
        if (element.values.includes('date')) {
          if (visualizeDateQuestions) {
            const label = this.getQuestionLabel(element);
            visualizedData.push({ position: element.position, type: 'Date', label });
          }
        } else if (element.values.includes('time')) {
          if (visualizeTimeQuestions) {
            const label = this.getQuestionLabel(element);
            visualizedData.push({ position: element.position, type: 'Time', label });
          }
        } else if (element.values.includes('datetime')) {
          if (visualizeDateTimeQuestions) {
            const label = this.getQuestionLabel(element);
            visualizedData.push({ position: element.position, type: 'DateTime', label });
          }
        }
      } else if (this.isBasicQuestionType(basicQuestionTypes, element.questiontype)) {
        const label = this.getQuestionLabel(element);
        visualizedData.push({ position: element.position, type: element.questiontype, label });
      }
    }
    return visualizedData;
  }

  private getVisualizableData(visualizedData: VisualizedData[]): VisualizableQuestion[] {
    const data: VisualizableQuestion[] = visualizedData.map(({ position, label }) => ({ position, label }));
    return data.sort((a, b) => a.position - b.position);
  }

  private getAnswerMap(
    diaryAnswersheets: AnswersheetInterface[],
    visualizedData: VisualizedData[],
  ): ChartDataPoint[] {
    const answerMap: ChartDataPoint[] = [];
    for (const answersheet of diaryAnswersheets) {
      const visualizedAnswers = new Map<number, AnswerData>();
      for (const { position, type } of visualizedData) {
        if (position === this.pedometerDataPosition) {
          const pedometer = answersheet.attributes.sensor_data?.pedometer;
          const hasValidSteps = pedometer?.status === 'success' && typeof pedometer.steps === 'number' && !Number.isNaN(pedometer.steps);
          const value = hasValidSteps ? pedometer.steps : null;
          visualizedAnswers.set(this.pedometerDataPosition, { value, type });
          continue;
        }
        const answer = answersheet.attributes.answers.find((a) => a.position === position);
        if (!answer) {
          visualizedAnswers.set(position, { value: null, type });
        } else {
          const value = answer.value[0];
          if (!value && typeof value !== 'number') {
            visualizedAnswers.set(position, { value: null, type });
          } else {
            switch (type) {
              case 'QuestionTable': {
                if (Array.isArray(value) && value.length > 0 && value.every((x) => x !== null)) {
                  const sum = value.reduce((x, y) => x + y, 0);
                  visualizedAnswers.set(position, { value: sum, type });
                } else {
                  visualizedAnswers.set(position, { value: null, type });
                }
                break;
              }
              case 'Date':
              case 'DateTime': {
                const date = new Date(value).getTime();
                visualizedAnswers.set(position, { value: date, type });
                break;
              }
              case 'Time': {
                const date = new Date(value);
                const time = date.getHours() * 60 + date.getMinutes();
                visualizedAnswers.set(position, { value: time, type });
                break;
              }
              default: {
                visualizedAnswers.set(position, { value, type });
              }
            }
          }
        }
      }
      this.addChartDataPoint(answerMap, answersheet, visualizedAnswers);
    }
    return answerMap;
  }

  private addChartDataPoint(
    chartDataPoints: ChartDataPoint[],
    answersheet: AnswersheetInterface,
    visualizedAnswers: Map<number, AnswerData>,
  ) {
    const newDataPoint: ChartDataPoint = {
      collectedAt: answersheet.attributes.collected_at * 1000,
      answers: visualizedAnswers,
    };
    for (let i = chartDataPoints.length - 1; i >= 0; i--) {
      const dataPoint = chartDataPoints[i];
      if (dataPoint.collectedAt <= newDataPoint.collectedAt) {
        chartDataPoints.splice(i + 1, 0, newDataPoint);
        return;
      }
    }
    chartDataPoints.splice(0, 0, newDataPoint);
  }

  selectedQuestionChanged() {
    this.updateChart();
  }

  private updateChart(): void {
    if (!this.chart) return;
    this.chart.destroy();
    setTimeout(() => {
      if (this.chartCanvas) {
        this.createChart(
          this.answerMap,
          this.visualizedData,
          this.visualizabledQuestion1,
          this.visualizabledQuestion2,
        );
      }
    }, 0);
  }

  isSelectedQuestion(question: VisualizableQuestion, selectedQuestion: VisualizableQuestion): boolean {
    return selectedQuestion != undefined && question.position === selectedQuestion.position;
  }

  positionEquals(a: VisualizableQuestion, b: VisualizableQuestion): boolean {
    return a.position === b.position;
  }

  filterBasicQuestionTypes(visualizableDataTypes: VisualizedDataTypes[]): BasicQuestionType[] {
    const result: BasicQuestionType[] = [];
    for (const dataType of visualizableDataTypes) {
      if (dataType !== 'SensorData_steps' && dataType !== 'TextDate_date' && dataType !== 'TextDate_time' && dataType !== 'TextDate_datetime') {
        result.push(dataType);
      }
    }
    return result;
  }

  isBasicQuestionType(basicQuestionTypes: BasicQuestionType[], type: string): type is BasicQuestionType {
    return basicQuestionTypes.includes(type as BasicQuestionType);
  }

  private getQuestionLabel(element: ElementInterface): string {
    if (!Array.isArray(element.translations) || element.translations.length === 0) {
      return '';
    }
    const lang = this.translateService.currentLang;
    const isQuestionTable = element.questiontype === 'QuestionTable';
    let result: string = isQuestionTable ? element.translations[0].question.join(', ') : element.translations[0].question;
    for (let i = 0; i < element.translations.length; i++) {
      const translation = element.translations[i];
      if (translation.locale === lang) {
        result = isQuestionTable ? translation.question.join(', ') : translation.question;
        break;
      } else if (translation.locale === 'en') {
        result = isQuestionTable ? translation.question.join(', ') : translation.question;
      }
    }
    return result;
  }

  private requestDiaryAnswersheets() {
    if (this.isManager) {
      this.diaryStore.getAnswersheetsOfDiaryECoachManager({
        diaryInstanceId: this.diaryInstanceId,
      });
    } else {
      this.diaryStore.getAnswersheetsOfDiary({
        diaryId: this.diary.id,
        diaryInstanceId: this.diaryInstanceId,
      });
    }
  }

  public loadAnswersheetPreview(result: AnswersheetInterface[]) {
    this.diaryAnswersheets = this.helper.getAnswersheetsByOldest(result);
    let selectedIndex: number = 0;
    if (this.diaryAnswersheets.length > 0) {
      this.hasDiaryEntriesSubject.next(true);
      selectedIndex = this.diaryAnswersheets.length - 1;
      this.initializeAnswersheetsPreviewComponent(this.diaryAnswersheets[selectedIndex]);
    }
    this.isLoadingSubject.next(false);
    this.selected.setValue(selectedIndex);
  }

  public initializeAnswersheetsPreviewComponent(answersheet: AnswersheetInterface): void {
    if (this.answersheetsDetailsPreviewContainer) {
      this.answersheetsDetailsPreviewContainer.clear();
      const component = this.answersheetsDetailsPreviewContainer.createComponent(DiaryAnswersheetsDetailComponent).instance;
      component._diary = this.diary;
      component._answersheet = answersheet;
      component._study_id = this.diary.attributes.study_id;
      component._diaryBackground = this.diary.attributes.page_color;
      component._inputElements = this.elements;
    }
  }

  public getSelectedIndexDiaries(event: number): void {
    if (this.diaryAnswersheets.length > 0 && event < this.diaryAnswersheets.length) {
      this.initializeAnswersheetsPreviewComponent(this.diaryAnswersheets[event]);
    }
  }

  public onPageChangeActivity(event: PageEvent): void {
    const startIndex = event.pageIndex * event.pageSize;
    const endIndex = Math.min(startIndex + event.pageSize, this.activitiesSubject.value.length);
    this.pagedActivitySubject.next(this.activitiesSubject.value.slice(startIndex, endIndex));
  }

  private getLegendFormatter(dataType: DataType) {
    switch (dataType) {
      case 'Date':
        return {
          callback: (value: number) => {
            const d = new Date(value);
            const lang = this.translateService.currentLang || 'en';
            return d.toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' });
          },
        };
      case 'Time':
        return {
          callback: (value: number) => {
            const mins = Math.round(value);
            const h = Math.floor(mins / 60).toString().padStart(2, '0');
            const m = (mins % 60).toString().padStart(2, '0');
            return `${h}:${m}`;
          },
          stepSize: 60,
        };
      case 'DateTime':
        return {
          callback: (value: number) => {
            const d = new Date(value);
            const lang = this.translateService.currentLang || 'en';
            const dateStr = d.toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' });
            const timeStr = d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
            return `${dateStr} ${timeStr}`;
          },
        };
      default:
        return {
          callback: (value: any) => {
            const rounded = Math.round(Number(value) * 2) / 2;
            return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(1);
          },
        };
    }
  }

  private createChart(
    answerMap: ChartDataPoint[],
    visualizableData: VisualizedData[],
    visualizabledQuestion1: VisualizableQuestion,
    visualizabledQuestion2?: VisualizableQuestion,
  ): void {
    const ctx = this.chartCanvas?.nativeElement?.getContext('2d');
    if (!ctx || (!visualizabledQuestion1 && !visualizabledQuestion2)) {
      return;
    }
    const position1: number = visualizabledQuestion1.position;
    const question1 = visualizableData.find((d) => d.position === position1);
    const data1 = answerMap.map((p) => p.answers.get(position1).value);
    const color1 = '#E6821E';
    const datasets = [{
      label: question1.label,
      data: data1,
      borderColor: color1,
      backgroundColor: this.hexToRgba(color1, 0.2),
      tension: 0,
      yAxisID: 'y',
      fill: false,
      borderWidth: 2,
      pointBackgroundColor: color1,
      spanGaps: true,
      pointStyle: 'circle',
      pointRadius: 3.5,
      pointHoverRadius: 5.5,
    }];
    const y = {
      type: 'linear',
      display: true,
      title: { display: false },
      ticks: {
        color: color1,
        ...this.getLegendFormatter(question1.type),
      },
      position: 'left',
    };

    let y1 = undefined;
    let question2 = undefined;
    if (visualizabledQuestion2) {
      const position2: number = visualizabledQuestion2.position;
      question2 = visualizableData.find((d) => d.position === position2);
      const data2 = answerMap.map((p) => p.answers.get(position2).value);
      const color2 = '#1E5FA8';
      datasets.push({
        label: question2.label,
        data: data2,
        borderColor: color2,
        backgroundColor: this.hexToRgba(color2, 0.2),
        tension: 0,
        yAxisID: 'y1',
        fill: false,
        borderWidth: 2,
        pointBackgroundColor: color2,
        spanGaps: true,
        pointStyle: 'rect',
        pointRadius: 6,
        pointHoverRadius: 8,
      });
      y1 = {
        type: 'linear',
        display: true,
        title: { display: false },
        ticks: {
          color: color2,
          ...this.getLegendFormatter(question2.type),
        },
        position: 'right',
        grid: { drawOnChartArea: false },
      };
    }
    const dateTimeFormatter = this.getLegendFormatter('DateTime').callback;
    const allDates = answerMap.map((a) => dateTimeFormatter(a.collectedAt));

    const yAxes: any = y1 ? { y, y1 } : { y };

    try {
      this.chart = new Chart(ctx, {
        type: 'line',
        data: { labels: allDates, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          onClick: (_event, elements) => {
            if (elements.length === 0) {
              this.chart?.tooltip?.setActiveElements([], { x: 0, y: 0 });
              this.chart?.update();
            }
          },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                generateLabels: (chart) => {
                  const original = Chart.defaults.plugins.legend?.labels?.generateLabels;
                  const labels = original ? original(chart) : [];
                  return labels.map(label => {
                    if (label.text && label.text.length > 40) {
                      label.text = label.text.substring(0, 37) + '...';
                    }
                    return label;
                  });
                },
              },
            },
            title: { display: false },
            tooltip: {
              displayColors: true,
              usePointStyle: true,
              multiKeyBackground: 'rgba(0,0,0,0.01)',
              boxHeight: 10,
              boxWidth: 10,
              padding: 12,
              callbacks: {
                title: (items): string => items[0]?.label || '',
                label: (context) => {
                  const questionType = context.datasetIndex === 0 ? question1.type : question2?.type;
                  const label = this.wrapTooltipText(context.dataset.label + ':');
                  const formatter = this.getLegendFormatter(questionType);
                  const value = this.wrapTooltipText(formatter.callback(context.parsed.y));
                  return label.concat(value);
                },
              },
            },
            zoom: {
              pan: { enabled: true, mode: 'y' },
              zoom: {
                wheel: { enabled: true, speed: 0.05 },
                pinch: { enabled: true },
                mode: 'y',
              },
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: this.translateService.instant('diary-answersheets.entry'),
              },
            },
            ...yAxes,
          },
        },
      });
      this.hasChartData = this.visualizableQuestions.length > 0;
    } catch (e) {
      console.error('[Chart] ERROR while building Chart constructor:', e);
    }
  }

  private wrapTooltipText(text: string, maxLineLength: number = 50): string[] {
    const words: string[] = text.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      return [''];
    }
    const lines: string[] = [];
    let currentLine = words[0];
    for (const word of words.slice(1)) {
      if ((currentLine.length + word.length) < maxLineLength) {
        currentLine += ' ' + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);
    return lines;
  }

  private hexToRgba(color: string, alpha: number): string {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  resetZoom(): void {
    this.chart?.resetZoom();
  }

  resetFirstDataFilter() {
    this.filter.filter1 = '';
    this.visualizableQuestions1Subject.next(this.visualizableQuestions);
  }

  resetSecondDataFilter() {
    this.filter.filter2 = '';
    this.visualizableQuestions2Subject.next(this.visualizableQuestions);
  }

  handleFirstDataFilter() {
    const input = this.filter.filter1.toLowerCase();
    const filteredData = this.visualizableQuestions.filter(
      (data) =>
        data.position === this.visualizabledQuestion1.position || data.label.toLowerCase().includes(input),
    );
    this.visualizableQuestions1Subject.next(filteredData);
  }

  handleSecondDataFilter() {
    const input = this.filter.filter2.toLowerCase();
    const filteredData = this.visualizableQuestions.filter(
      (data) =>
        data.position === this.visualizabledQuestion2.position || data.label.toLowerCase().includes(input),
    );
    this.visualizableQuestions2Subject.next(filteredData);
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
